import React, { useEffect, useState } from 'react';
import { nb } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { addDays, formatDateShort, isoToLocalDate, isValidDate, localDateToIso, normalizeTime, osloDateOf, parseNbDate } from '@/domain/time';
import { cn } from '@/lib/utils';

/*
  Dato- og klokkeslettfelt med entydig norsk visning uavhengig av nettleserspråk:
  «27.10.2026» og «07:00–15:00» – aldri «10/27/2026» eller «07:00 AM». Nettleserens innebygde date/time-felt følger
  nettleserens språk, derfor brukes tekstfelt som tolker norsk skrivemåte + kalender (nb-NO) i popover.
  Verdiene som lagres er fortsatt ISO «YYYY-MM-DD» og «HH:MM».
*/

const WRAP = 'flex min-w-0 items-stretch overflow-hidden rounded-[10px] border border-[var(--hr-border)] bg-white shadow-none transition-[border-color,box-shadow] duration-150 focus-within:border-[var(--hr-focus)] focus-within:ring-2 focus-within:ring-[var(--hr-focus)]';
const TEXT = 'hr-tabular min-w-0 flex-1 bg-transparent px-3 text-[16px] text-[var(--hr-text)] outline-none placeholder:text-[var(--hr-muted-2)] disabled:cursor-not-allowed disabled:opacity-60 lg:text-[14px]';

/**
 * Datofelt: tekst «dd.mm.åååå» (tolker også d.m.åå og ISO) + kalender. Snarveier «I dag» / «I går» i kalenderen.
 * `value` er ISO 'YYYY-MM-DD'. Ugyldig tekst tilbakestilles til gjeldende verdi ved blur.
 */
export function NbDateInput({ id, value, onChange, min, max, disabled, size = 'form', testId, ariaLabel = 'Dato', className, showQuick = true }) {
  const [text, setText] = useState(value ? formatDateShort(value) : '');
  const [open, setOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  useEffect(() => {
    setText(value && isValidDate(value) ? formatDateShort(value) : '');
    setInvalid(false);
  }, [value]);

  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const commit = () => {
    const iso = parseNbDate(text);
    if (iso && inRange(iso)) {
      setInvalid(false);
      if (iso !== value) onChange(iso);
      else setText(formatDateShort(iso));
    } else if (!text.trim()) {
      setInvalid(false);
      setText(value ? formatDateShort(value) : '');
    } else {
      setInvalid(true);
    }
  };
  const pick = (iso) => {
    if (!inRange(iso)) return;
    onChange(iso);
    setOpen(false);
  };
  const today = osloDateOf(new Date());
  const compact = size === 'compact';

  return (
    <div className={cn(WRAP, compact ? 'h-9' : 'h-11 lg:h-10', invalid && 'border-[var(--hr-red)] focus-within:border-[var(--hr-red)] focus-within:ring-[var(--hr-red)]/30', disabled && 'opacity-60', className)} data-testid={testId ? `${testId}-wrap` : undefined}>
      <input
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            setText(value ? formatDateShort(value) : '');
            setInvalid(false);
          }
        }}
        inputMode="numeric"
        placeholder="dd.mm.åååå"
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        className={cn(TEXT, compact && 'px-2.5 text-[14px] font-medium lg:text-[14px]')}
        data-testid={testId}
        data-iso={value || ''}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={`Åpne kalender for ${ariaLabel.toLowerCase()}`}
            className={cn('flex shrink-0 items-center justify-center border-l border-[var(--hr-hairline)] text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-surface-2)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:bg-[var(--hr-accent-soft)] disabled:cursor-not-allowed', compact ? 'w-9' : 'w-11 lg:w-10')}
            data-testid={testId ? `${testId}-calendar` : undefined}
          >
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto rounded-[12px] p-0 shadow-[var(--hr-shadow-md)]" data-testid={testId ? `${testId}-calendar-popover` : undefined}>
          {showQuick && (
            <div className="flex gap-1.5 border-b border-[var(--hr-hairline)] px-3 py-2">
              {[
                { label: 'I dag', iso: today },
                { label: 'I går', iso: addDays(today, -1) },
              ]
                .filter((q) => inRange(q.iso))
                .map((q) => (
                  <button key={q.label} type="button" onClick={() => pick(q.iso)} className={cn('h-8 rounded-full border px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]', value === q.iso ? 'border-[var(--hr-heading)] bg-[var(--hr-heading)] text-white' : 'border-[var(--hr-border)] bg-white text-[var(--hr-text)] hover:bg-[var(--hr-surface-2)]')} data-testid={testId ? `${testId}-quick-${q.label === 'I dag' ? 'today' : 'yesterday'}` : undefined}>
                    {q.label}
                  </button>
                ))}
            </div>
          )}
          <Calendar
            mode="single"
            locale={nb}
            weekStartsOn={1}
            selected={value && isValidDate(value) ? isoToLocalDate(value) : undefined}
            defaultMonth={isoToLocalDate(value && isValidDate(value) ? value : today)}
            fromDate={min ? isoToLocalDate(min) : undefined}
            toDate={max ? isoToLocalDate(max) : undefined}
            onSelect={(d) => d && pick(localDateToIso(d))}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Klokkeslett i 24-timers format («07:00»). Tekstfelt med talltastatur som tolker «7», «730», «7.30» → «07:00»/«07:30».
 * Ugyldig tekst beholdes synlig og markeres, og sendes rått videre slik at skjemaets validering kan si fra.
 */
export function TimeInput({ id, value, onChange, disabled, testId, ariaLabel = 'Klokkeslett', className, placeholder = '07:00' }) {
  const [text, setText] = useState(value ?? '');
  useEffect(() => {
    setText((t) => (normalizeTime(t) === value || t === value ? t : value ?? ''));
  }, [value]);
  const normalized = normalizeTime(text);
  const invalid = text.trim() !== '' && normalized === null;

  const commit = () => {
    const n = normalizeTime(text);
    if (n) {
      setText(n);
      if (n !== value) onChange(n);
    } else {
      onChange(text.trim());
    }
  };

  return (
    <input
      id={id}
      value={text}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        // Ferdig skrevet «HH:MM» tas i bruk med én gang (slik at «Send inn» uten blur også får verdien).
        if (/^\d{2}:\d{2}$/.test(t) && normalizeTime(t)) onChange(t);
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      className={cn(
        'hr-tabular h-11 w-full min-w-0 rounded-[10px] border bg-white px-3 text-[16px] text-[var(--hr-text)] shadow-none outline-none transition-[border-color,box-shadow] duration-150 placeholder:text-[var(--hr-muted-2)] focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-60 lg:h-10 lg:text-[14px]',
        invalid ? 'border-[var(--hr-red)] focus-visible:border-[var(--hr-red)] focus-visible:ring-[var(--hr-red)]/30' : 'border-[var(--hr-border)] focus-visible:border-[var(--hr-focus)] focus-visible:ring-[var(--hr-focus)]',
        className,
      )}
      data-testid={testId}
    />
  );
}
